import { FC, ReactNode, useEffect, useMemo, useRef } from "react"
import { Fragment } from "react/jsx-runtime"
import AdminPagination from "./AdminPagination";
import { UseAppTableProps } from "hooks/useAppTable";
import AppEmpty from "./AppEmpty";


export interface AppTableColumnProps<T> {
    width?: string | number;
    hidden?: boolean;
    key: keyof T;
    label: ReactNode;
    textAlign?: "left" | "center" | "right";
    textAlignHead?: "left" | "center" | "right";
    textAlignBody?: "left" | "center" | "right";
    render?: (row: T) => ReactNode;
}
interface AppTableProps<T> {
    appTable: UseAppTableProps<T>;
    rowSelection?: boolean;
    columns: Array<AppTableColumnProps<T>>;
    rowClassName?: (row: T) => string;
    cellClassName?: (row: T) => string;
}
const AppTable = <T extends object>({
    appTable,
    rowSelection = true,
    ...props
}: AppTableProps<T>) => {
    const columns = props.columns.filter(col => !col.hidden);
    const colSpan = useMemo(() => {
        return columns.length + 1 + (rowSelection ? 1 : 0);
    }, [columns.length, rowSelection])
    const checkboxRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (checkboxRef.current) {
            if (!appTable.isSelectedAll && appTable.selecteds.length > 0) {
                checkboxRef.current.indeterminate = true;
            }
            else {
                checkboxRef.current.indeterminate = false;
            }
        }

    }, [appTable.isSelectedAll, checkboxRef, appTable.selecteds])

    return <Fragment>
        <div className="bg-white shadow rounded">
            <table className="min-w-full table-auto rounded">
                <thead className="text-left text-white">
                    <tr>
                        {rowSelection && <th className="bg-blue-800 px-4 py-3 w-12 rounded-tl">
                            <input
                                type="checkbox"
                                className="accent-blue-600"
                                onClick={() => appTable.handleSelectAll()}
                                checked={appTable.isSelectedAll}
                                ref={checkboxRef}
                            />
                        </th>}
                        <th className={`bg-blue-800 px-4 py-3 w-12 ${!rowSelection ? "rounded-tl" : ""}`}>#</th>
                        {columns.map((col, i) => <th key={i} style={{ width: col.width }} className={`bg-blue-800 px-4 py-3 relative text-${col.textAlignHead || col.textAlign || "left"} ${i === columns.length - 1 ? "rounded-tr" : ""}`}>{col.label}</th>)}
                    </tr>
                </thead>
                <tbody className="text-gray-700">
                    {appTable.dataSource.map((item, i) => {
                        let stt: number = i + 1;
                        if (appTable.pagination) {
                            stt = appTable.pagination.pageSize * (appTable.pagination.page - 1) + i + 1
                        }
                        const selected = appTable.selecteds.findIndex(s => s[appTable.rowKey] === item[appTable.rowKey]) >= 0;
                        const tdClass = `px-4 py-3 group-hover/row:bg-gray-50 ${selected ? "bg-blue-50 group-hover/row:bg-blue-50" : ""}`;

                        const rowClass = `${props.rowClassName?.(item) || ""} border-t group/row`;
                        const cellClass = `${props.cellClassName?.(item) || ""}`;
                        return <tr className={rowClass}>
                            {rowSelection && <td className={`${tdClass} ${cellClass}`}>
                                <input
                                    type="checkbox"
                                    className="rowCheckbox accent-blue-600"
                                    onClick={() => appTable.handleSelect(item)}
                                    checked={selected}
                                />
                            </td>}
                            <td className={`${tdClass} ${cellClass}`}>{stt}</td>
                            {columns.map((col, j) => {
                                const rowKey = col.key as keyof T;
                                const _item = item as any;
                                const value = (col.render && col.render(item)) || _item[rowKey];
                                return <td key={`${i}-${j}`} style={{ width: col.width }} className={`${tdClass} text-${col.textAlignBody || col.textAlign || "left"} ${cellClass}`} >{value}</td>
                            })}
                        </tr>
                    })}
                    {appTable.dataSource.length === 0 && <tr className="border-t">
                        <td className="px-4 py-3 text-center" colSpan={colSpan}>
                            {/* Không có dữ liệu */}
                            <AppEmpty />
                        </td>
                    </tr>}
                </tbody>
            </table>
        </div>
        <AdminPagination page={appTable.pagination?.page} pageSize={appTable.pagination?.pageSize} total={appTable.pagination?.total} />
    </Fragment>
}

export default AppTable;